import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import { useAuth } from '../context/AuthContext';
import {
  FaBriefcase,
  FaCheckCircle,
  FaHistory,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaRupeeSign,
  FaList,
  FaTh,
} from 'react-icons/fa';
import client from '../api/client';
import { toast } from 'react-toastify';
import {
  mapDoctorVisitFromApi,
  mergeDoctorVisitContact,
  getVisitStatusBadgeClass,
  formatPostedAgo,
} from '../Utils/doctorVisits';
import '../App.css';

function PatientContactFields({ visit }) {
  return (
    <>
      <div className="col-12"><hr /></div>
      <div className="col-12">
        <h6 className="mb-2">Patient Contact</h6>
      </div>
      <div className="col-md-6">
        <strong>Patient Name</strong>
        <div>{visit.patientName || '—'}</div>
      </div>
      <div className="col-md-6">
        <strong>Phone</strong>
        <div>
          {visit.patientMobile ? (
            <a href={`tel:${visit.patientMobile}`}>{visit.patientMobile}</a>
          ) : (
            '—'
          )}
        </div>
      </div>
      <div className="col-md-6">
        <strong>Email</strong>
        <div>
          {visit.patientEmail ? (
            <a href={`mailto:${visit.patientEmail}`}>{visit.patientEmail}</a>
          ) : (
            '—'
          )}
        </div>
      </div>
      <div className="col-12">
        <strong>Address</strong>
        <div>{visit.patientAddress || '—'}</div>
      </div>
      {visit.landmark && (
        <div className="col-12">
          <strong>Landmark</strong>
          <div>{visit.landmark}</div>
        </div>
      )}
    </>
  );
}

function ProfessionalVisitRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('available');
  const [districtFilter, setDistrictFilter] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [isDistrictInitialized, setIsDistrictInitialized] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchText, setSearchText] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

  const [availableVisits, setAvailableVisits] = useState([]);
  const [acceptedVisits, setAcceptedVisits] = useState([]);
  const [historyVisits, setHistoryVisits] = useState([]);

  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [isLoadingAccepted, setIsLoadingAccepted] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [completeRemarks, setCompleteRemarks] = useState('');

  const fetchDistricts = async () => {
    setIsLoadingDistricts(true);
    try {
      const response = await client.get('/api/districts');
      setDistricts(response.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load districts.');
    } finally {
      setIsLoadingDistricts(false);
    }
  };

  const fetchCurrentProfessional = async () => {
    try {
      const response = await client.get('/api/healthcare-professionals/current');
      const data = response.data?.data;
      if (data?.idDistrict && data.idDistrict > 0) {
        setDistrictFilter(data.idDistrict);
      }
    } catch {
      // default All
    } finally {
      setIsDistrictInitialized(true);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await client.get('/api/service-types');
      if (response.data?.success) {
        setServiceTypes(response.data.data || []);
      }
    } catch {
      setServiceTypes([]);
    }
  };

  const fetchAvailable = useCallback(async () => {
    setIsLoadingAvailable(true);
    try {
      const params = {};
      if (districtFilter) params.idDistrict = districtFilter;
      if (serviceFilter) params.serviceTypeId = serviceFilter;
      if (searchText.trim()) params.search = searchText.trim();
      const response = await client.get('/api/healthcare-professionals/visit-requests/available', { params });
      const list = (response.data?.data || []).map(mapDoctorVisitFromApi);
      setAvailableVisits(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load available visits.');
      setAvailableVisits([]);
    } finally {
      setIsLoadingAvailable(false);
    }
  }, [districtFilter, serviceFilter, searchText]);

  const fetchAccepted = useCallback(async () => {
    setIsLoadingAccepted(true);
    try {
      const params = {};
      if (districtFilter) params.idDistrict = districtFilter;
      const response = await client.get('/api/healthcare-professionals/visit-requests/accepted', { params });
      const list = (response.data?.data || []).map(mapDoctorVisitFromApi);
      setAcceptedVisits(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load accepted visits.');
      setAcceptedVisits([]);
    } finally {
      setIsLoadingAccepted(false);
    }
  }, [districtFilter]);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const params = { status: historyStatusFilter };
      if (districtFilter) params.idDistrict = districtFilter;
      if (searchText.trim()) params.search = searchText.trim();
      const response = await client.get('/api/healthcare-professionals/visit-requests/history', { params });
      const list = (response.data?.data || []).map(mapDoctorVisitFromApi);
      setHistoryVisits(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load visit history.');
      setHistoryVisits([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [districtFilter, historyStatusFilter, searchText]);

  const openViewModal = async (visit) => {
    try {
      const response = await client.get(`/api/healthcare-professionals/visit-requests/${visit.id}`);
      if (response.data?.success) {
        const detail = mapDoctorVisitFromApi(response.data.data);
        setSelectedVisit(mergeDoctorVisitContact(detail, visit));
        setShowViewModal(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load visit details.');
    }
  };

  const handleDistrictChange = (e) => {
    const val = e.target.value;
    if (val === 'All' || !val) {
      setDistrictFilter(null);
    } else {
      setDistrictFilter(Number(val));
    }
    if (!isDistrictInitialized) setIsDistrictInitialized(true);
  };

  const handleAccept = async () => {
    if (!selectedVisit) return;
    setIsAccepting(true);
    try {
      const response = await client.post('/api/healthcare-professionals/visit-requests/accept', {
        idVisitRequest: selectedVisit.id,
      });
      if (response.data?.success) {
        toast.success(response.data?.message || 'Visit accepted successfully.');
        setShowAcceptModal(false);
        setSelectedVisit(null);
        await fetchAvailable();
        await fetchAccepted();
        setActiveTab('accepted');
      } else {
        toast.error(response.data?.message || 'Failed to accept visit.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to accept visit.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedVisit) return;
    if (!completeRemarks.trim()) {
      toast.error('Please enter consultation remarks.');
      return;
    }
    setIsCompleting(true);
    try {
      const response = await client.post('/api/healthcare-professionals/visit-requests/complete', {
        idVisitRequest: selectedVisit.id,
        consultationRemarks: completeRemarks.trim(),
      });
      if (response.data?.success) {
        toast.success(response.data?.message || 'Visit marked as completed.');
        setShowCompleteModal(false);
        setCompleteRemarks('');
        setSelectedVisit(null);
        await fetchAccepted();
        await fetchHistory();
        setActiveTab('history');
      } else {
        toast.error(response.data?.message || 'Failed to complete visit.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to complete visit.');
    } finally {
      setIsCompleting(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
    fetchServiceTypes();
  }, []);

  useEffect(() => {
    if (districts.length > 0) {
      fetchCurrentProfessional();
    }
  }, [districts]);

  useEffect(() => {
    if (!isDistrictInitialized) return;
    if (activeTab === 'available') fetchAvailable();
    if (activeTab === 'accepted') fetchAccepted();
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, isDistrictInitialized, districtFilter, fetchAvailable, fetchAccepted, fetchHistory]);

  const renderVisitCard = (visit, { showAccept, showComplete }) => (
    <div key={visit.id} className="col-12 col-md-6 col-lg-4">
      <div className="card h-100 opportunity-card modern-card">
        <div className="card-body d-flex flex-column p-0">
          <div className="card-header-section">
            <h5 className="card-position-title">{visit.serviceType}</h5>
            <span className={getVisitStatusBadgeClass(visit.status)}>{visit.statusLabel}</span>
          </div>
          <div className="card-info-section">
            <div className="info-item">
              <div className="info-icon location-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="info-content">
                <div className="info-label">Location</div>
                <div className="info-value">{visit.locationLabel}</div>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <FaCalendarAlt />
              </div>
              <div className="info-content">
                <div className="info-label">Visit Date</div>
                <div className="info-value">
                  {visit.preferredDate} at {visit.preferredTime}
                </div>
              </div>
            </div>
            {visit.publishedAt && activeTab === 'available' && (
              <div className="info-item">
                <div className="info-icon">
                  <FaClock />
                </div>
                <div className="info-content">
                  <div className="info-label">Posted</div>
                  <div className="info-value">{formatPostedAgo(visit.publishedAt)}</div>
                </div>
              </div>
            )}
            <div className="info-item">
              <div className="info-icon">
                <FaRupeeSign />
              </div>
              <div className="info-content">
                <div className="info-label">Rate</div>
                <div className="info-value">{visit.amount}</div>
              </div>
            </div>
          </div>
          {visit.description && (
            <div className="px-3 pb-2">
              <small className="text-muted d-block" style={{ lineHeight: 1.4 }}>
                {visit.description.length > 120
                  ? `${visit.description.slice(0, 120)}…`
                  : visit.description}
              </small>
            </div>
          )}
          <div className="card-divider" />
          <div className="card-details-section p-3 pt-0">
            <div className="d-flex flex-column gap-2">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={() => openViewModal(visit)}
              >
                View Details
              </button>
              {showAccept && (
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => {
                    setSelectedVisit(visit);
                    setShowAcceptModal(true);
                  }}
                >
                  Accept Job
                </button>
              )}
              {showComplete && (
                <button
                  type="button"
                  className="btn btn-success w-100"
                  onClick={() => {
                    setSelectedVisit(visit);
                    setCompleteRemarks('');
                    setShowCompleteModal(true);
                  }}
                >
                  Mark as Completed
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const isLoading =
    (activeTab === 'available' && isLoadingAvailable)
    || (activeTab === 'accepted' && isLoadingAccepted)
    || (activeTab === 'history' && isLoadingHistory);

  const currentList =
    activeTab === 'available'
      ? availableVisits
      : activeTab === 'accepted'
        ? acceptedVisits
        : historyVisits;

  return (
    <div className="App">
      <DashboardHeader
        userType={(user?.role || 'professional')?.toLowerCase()}
        userName={user?.displayName || 'Professional'}
        onMenuClick={(menuItem) => {
          if (menuItem === 'opportunities' || menuItem === 'applied') {
            navigate('/professional-dashboard');
          }
          if (menuItem === 'visit-requests') {
            setActiveTab('available');
          }
          if (menuItem === 'modify-profile') {
            navigate('/professional-profile');
          }
        }}
      />

      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="dashboard-nav mb-4 flex-wrap">
                <button
                  type="button"
                  className={`dashboard-nav-btn ${activeTab === 'available' ? 'active' : ''}`}
                  onClick={() => setActiveTab('available')}
                >
                  <FaBriefcase className="me-2" />
                  Available Jobs
                </button>
                <button
                  type="button"
                  className={`dashboard-nav-btn ${activeTab === 'accepted' ? 'active' : ''}`}
                  onClick={() => setActiveTab('accepted')}
                >
                  <FaCheckCircle className="me-2" />
                  Accepted Jobs
                </button>
                <button
                  type="button"
                  className={`dashboard-nav-btn ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  <FaHistory className="me-2" />
                  Visit History
                </button>
              </div>

              <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title mb-2">
                  {activeTab === 'available' && 'Available Jobs'}
                  {activeTab === 'accepted' && 'Accepted Jobs'}
                  {activeTab === 'history' && 'Visit History'}
                </h2>
                {activeTab !== 'history' && (
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="row align-items-end g-2">
                  <div className="col-md-3">
                    <label htmlFor="visitDistrictFilter" className="form-label">District</label>
                    <select
                      className="form-select"
                      id="visitDistrictFilter"
                      value={districtFilter || 'All'}
                      onChange={handleDistrictChange}
                      disabled={isLoadingDistricts}
                    >
                      <option value="All">All Districts</option>
                      {districts.map((d) => (
                        <option key={d.idDistrict} value={d.idDistrict}>
                          {d.districtName}
                        </option>
                      ))}
                    </select>
                  </div>
                  {activeTab === 'available' && (
                    <div className="col-md-3">
                      <label htmlFor="visitServiceFilter" className="form-label">Service</label>
                      <select
                        className="form-select"
                        id="visitServiceFilter"
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                      >
                        <option value="">All Services</option>
                        {serviceTypes.map((s) => (
                          <option key={s.idServiceType} value={s.idServiceType}>
                            {s.serviceName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {activeTab === 'history' && (
                    <div className="col-md-3">
                      <label htmlFor="historyStatusFilter" className="form-label">Status</label>
                      <select
                        className="form-select"
                        id="historyStatusFilter"
                        value={historyStatusFilter}
                        onChange={(e) => setHistoryStatusFilter(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="completed">Completed</option>
                        <option value="paid">Paid &amp; Closed</option>
                      </select>
                    </div>
                  )}
                  {(activeTab === 'available' || activeTab === 'history') && (
                    <div className="col-md-4">
                      <label htmlFor="visitSearch" className="form-label">Search</label>
                      <input
                        type="text"
                        className="form-control"
                        id="visitSearch"
                        placeholder="Service, location, booking…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                  )}
                  {(activeTab === 'available' || activeTab === 'history') && (
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={() => {
                          if (activeTab === 'available') fetchAvailable();
                          else fetchHistory();
                        }}
                      >
                        Search
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {activeTab === 'history' || viewMode === 'table' ? (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Booking</th>
                            <th>Service</th>
                            <th>Visit Date</th>
                            <th>Mode</th>
                            <th>Location</th>
                            {activeTab !== 'available' && <th>Remarks</th>}
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="8" className="text-center">Loading…</td>
                            </tr>
                          ) : currentList.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center">No visits found</td>
                            </tr>
                          ) : (
                            currentList.map((visit) => (
                              <tr key={visit.id}>
                                <td>{visit.bookingNumber}</td>
                                <td>{visit.serviceType}</td>
                                <td>
                                  {visit.preferredDate}
                                  <br />
                                  <small className="text-muted">{visit.preferredTime}</small>
                                </td>
                                <td>{visit.visitMode}</td>
                                <td>{visit.locationLabel}</td>
                                {activeTab !== 'available' && (
                                  <td>
                                    <small>{visit.consultationRemarks || '—'}</small>
                                  </td>
                                )}
                                <td>
                                  <span className={getVisitStatusBadgeClass(visit.status)}>
                                    {visit.statusLabel}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => openViewModal(visit)}
                                    >
                                      View
                                    </button>
                                    {activeTab === 'available' && (
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => {
                                          setSelectedVisit(visit);
                                          setShowAcceptModal(true);
                                        }}
                                      >
                                        Accept
                                      </button>
                                    )}
                                    {activeTab === 'accepted' && (
                                      <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        onClick={() => {
                                          setSelectedVisit(visit);
                                          setCompleteRemarks('');
                                          setShowCompleteModal(true);
                                        }}
                                      >
                                        Complete
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="row g-3">
                  {isLoading ? (
                    <div className="col-12 text-center py-5 text-muted">Loading…</div>
                  ) : currentList.length === 0 ? (
                    <div className="col-12 text-center py-5 text-muted">No visits found</div>
                  ) : (
                    currentList.map((visit) =>
                      renderVisitCard(visit, {
                        showAccept: activeTab === 'available',
                        showComplete: activeTab === 'accepted',
                      }),
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Visit Details — {selectedVisit?.bookingNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVisit && (
            <div className="row g-3">
              <div className="col-md-6">
                <strong>Service</strong>
                <div>{selectedVisit.serviceType}</div>
              </div>
              <div className="col-md-6">
                <strong>Status</strong>
                <div>
                  <span className={getVisitStatusBadgeClass(selectedVisit.status)}>
                    {selectedVisit.statusLabel}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <strong>Date &amp; Time</strong>
                <div>
                  {selectedVisit.preferredDate} at {selectedVisit.preferredTime}
                </div>
              </div>
              <div className="col-md-6">
                <strong>Rate</strong>
                <div>{selectedVisit.amount}</div>
              </div>
              <div className="col-12">
                <strong>Location</strong>
                <div>{selectedVisit.locationLabel}</div>
              </div>
              <div className="col-12">
                <strong>Description / Symptoms</strong>
                <div className="text-muted">{selectedVisit.description || '—'}</div>
              </div>
              <PatientContactFields visit={selectedVisit} />
              {selectedVisit.consultationRemarks && (
                <div className="col-12">
                  <strong>Consultation Remarks</strong>
                  <div>{selectedVisit.consultationRemarks}</div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedVisit?.canAccept && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowViewModal(false);
                setShowAcceptModal(true);
              }}
            >
              Accept Job
            </button>
          )}
          {selectedVisit?.canComplete && (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                setShowViewModal(false);
                setCompleteRemarks('');
                setShowCompleteModal(true);
              }}
            >
              Mark as Completed
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAcceptModal} onHide={() => setShowAcceptModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Accept Visit Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVisit && (
            <p>
              Accept <strong>{selectedVisit.serviceType}</strong> on{' '}
              <strong>{selectedVisit.preferredDate}</strong> at{' '}
              <strong>{selectedVisit.preferredTime}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setShowAcceptModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleAccept} disabled={isAccepting}>
            {isAccepting ? 'Accepting…' : 'Confirm Accept'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Visit as Completed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label htmlFor="consultationRemarks" className="form-label">
            Consultation Remarks <span className="text-danger">*</span>
          </label>
          <textarea
            id="consultationRemarks"
            className="form-control"
            rows={4}
            value={completeRemarks}
            onChange={(e) => setCompleteRemarks(e.target.value)}
            placeholder="Summary of consultation, prescriptions, follow-up advice…"
          />
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setShowCompleteModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-success" onClick={handleComplete} disabled={isCompleting}>
            {isCompleting ? 'Saving…' : 'Mark Completed'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ProfessionalVisitRequests;
